<template>
    <div>
        <label
            class="d-flex text-uppercase font-weight-bold"
        >
            Customization
        </label>
        <div
            v-if="savedBundles?.length"
        >
            <v-item-group
                :model-value="bundleViewMode"
                class="option-btn-group-v2 option-btn-tall"
                @update:model-value="onBundleViewModeChange"
                mandatory
            >
                <v-item
                    value="customize"
                    #default="{ isSelected, toggle }"
                >
                    <v-btn
                        variant="outlined"
                        class="rounded"
                        :class="{ 'selected': isSelected }"
                        @click="toggle"
                    >
                        <strong>New</strong>
                    </v-btn>
                </v-item>
                <v-item
                    value="saved"
                    #default="{ isSelected, toggle }"
                >
                    <v-btn
                        variant="outlined"
                        class="rounded"
                        :class="{ 'selected': isSelected }"
                        @click="toggle"
                    >
                        <strong>Saved</strong>
                    </v-btn>
                </v-item>
            </v-item-group>
        </div>
        <v-item-group
            v-if="bundleViewMode === 'saved' && savedBundles?.length"
            class="option-btn-group-v2 option-btn-var-height saved-bundle-grid mt-6 mb-2"
            :model-value="selectedBundle"
            @update:model-value="onSelectionChange"
            mandatory
        >
            <v-item
                v-for="bundle in savedBundles"
                :key="`saved-bundle-${bundle.uid}`"
                :value="bundle"
                #default="{ isSelected, toggle, value }"
            >
                <div>
                    <v-btn
                        variant="outlined"
                        class="rounded aspect-ratio-1 position-relative"
                        :class="{ 'selected': isSelected }"
                        :aria-label="bundle.name"
                        @click="toggle"
                    >
                        <FullImage
                            :image-object="bundle.image"
                            class="w-100"
                            type="large"
                            disable-drop-shadow
                        ></FullImage>

                        <template
                            #append
                        >
                            <div
                                class="btn-icons position-absolute"
                            >
                                <v-icon
                                    class="saved-bundle-visibility-icon"
                                    size="14"
                                    :icon="bundle.visibility === VisibilityRole.USER ? mdiEyeOff : mdiEye"
                                    :color="bundle.visibility === VisibilityRole.USER ? 'grey-lighten-1' : 'primary'"
                                ></v-icon>
                            </div>
                        </template>
                    </v-btn>
                    <span
                        class="saved-bundle-name text-body-small text-center d-block mt-1 text-truncate"
                        :class="{ 'font-weight-bold': selectedBundle?.uid === bundle.uid }"
                    >
                        {{ bundle.name }}
                    </span>
                </div>
            </v-item>
        </v-item-group>
        <div
            v-if="bundleViewMode === 'saved' && isLoggedIn"
            class="d-flex justify-end mb-2"
        >
            <v-btn
                to="/account/customizations"
                color="secondary"
                size="small"
                variant="text"
            >
                Manage Customizations
                <v-icon
                    :icon="mdiExitToApp"
                    class="ml-1"
                ></v-icon>
            </v-btn>
        </div>
        <div>
            <BundleTemplateSummary
                v-if="bundleViewMode === 'saved' && selectedBundle"
                :bundle="selectedBundle"
            ></BundleTemplateSummary>
            <div
                v-if="bundleViewMode === 'customize'"
                class="d-flex justify-end mb-2"
            >
                <v-btn
                    :prepend-icon="mdiRestore"
                    variant="text"
                    size="small"
                    color="secondary"
                    @click="onDefaultReset"
                >
                    Reset to Defaults
                </v-btn>
            </div>
            <slot
                name="templates"
                :show-templates="bundleViewMode === 'customize'"
            ></slot>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import type {
        Bundle,
        BundleViewMode
    } from '~/types';
    import {
        mdiEye,
        mdiEyeOff,
        mdiExitToApp,
        mdiRestore
    } from '@mdi/js';
    import {
        VisibilityRole
    } from '~/types';

    import BundleTemplateSummary from '~/components/products/product/bundle-templates/BundleTemplateSummary/BundleTemplateSummary.vue';
    const FullImage = defineAsyncComponent(() => import('~/components/media/FullImage/FullImage.vue'));

    const nuxtApp = useNuxtApp();
    const { isLoggedIn } = nuxtApp.$user;

    const props = defineProps<{
        savedBundles: Bundle[];
        bundleViewMode: BundleViewMode;
        selectedBundle: Nullable<Bundle>;
        currentBundleTemplateUid: Nullable<string>;
    }>();

    const emit = defineEmits<{
        'update:bundleViewMode': [value: BundleViewMode];
        'on-selection-change': [value: Bundle];
        'on-default-reset': [value: void];
    }>();

    function onSelectionChange(bundle: Nilish<Bundle>): void {
        if (bundle) {
            emit('on-selection-change', bundle);
        }
    }

    function onDefaultReset(): void {
        emit('on-default-reset');
    }

    function onBundleViewModeChange(value: BundleViewMode): void {
        emit('update:bundleViewMode', value);
    }
</script>

<style lang="scss">
$saved-button-row-height-min: 84px;

.saved-bundle-grid {
    grid-template-columns: repeat(auto-fill, minmax($saved-button-row-height-min, 1fr));

    .v-btn {
        width: 100%;
        height: auto !important;
    }

    .btn-icons {
        top: 4px;
        right: 4px;
        z-index: 1;
    }
}
</style>
